import * as React from "react";

import {CheckIcon} from "@heroicons/react/20/solid";
import Decimal from "decimal.js";
import {produce} from "immer";

import * as dialogs from "@client/features/dialogs";
import {notify} from "@client/features/notifications";
import {showFeelerIfFirstTime} from "@client/features/scenes";
import {
    WorkoutContext,
    annotateWithExercises,
    useWorkout,
} from "@client/features/workouts";

import * as dates from "@client/dates";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import * as utils from "@client/utils";

import {GuidedWorkout} from "@client/components/GuidedWorkout";
import {ContentModal} from "@client/components/Modal";
import {ProgramCard} from "@client/components/ProgramCard";
import {
    InstructionsModal,
    Workout,
    WorkoutActions,
    getPercentageExercises,
    processResponse,
} from "@client/components/Workout";
import {ProgramWizard} from "@client/components/WorkoutProgram";
import {Dialogs} from "@client/containers/Dialogs";
import * as widgets from "@client/forms/widgets";
import * as hardware from "@client/hardware";
import {useSerializedSave} from "@client/hooks";
import type {Props as JournalProps} from "@client/scenes/Journal";
import * as forms from "@client/site/forms";
import {useAppDispatch} from "@client/store";
import {classNames, models, server} from "@reactivated";

type Router = {
    route: JournalProps["workoutRoute"];
    setRoute: JournalProps["setWorkoutRoute"];
};

interface Options {
    updateJournal: (
        value: (
            prev: server.journal.day.Journal.output,
        ) => server.journal.day.Journal.output,
    ) => void;
    switchTab: (tab: "workout" | "journal") => void;
    router: Router;
    program_templates: server.manager.templates.ProgramTemplate.output[];
    exercise_maxes: models["journal.workouts.ExerciseMaxes"];
    templates: server.journal.workouts.WorkoutTemplate.output[];
    onCopy: (pickedDate: DateValue) => void | Promise<void>;
    setResolved?: (
        resolved: Partial<{
            workout: server.journal.workouts.Workout.output | null;
            program: server.journal.workouts.Program.output | null;
        }>,
    ) => void;
    scope: "consumer" | "client" | "manager";
    target: {
        user_id: number;
        key: string;
    };
    startRestTimer: (duration: number | null) => void;
    deleteWorkout: () => Promise<void>;
}

function CreateWorkout(props: {
    activeProgram: server.journal.workouts.ActiveProgram.output | null;
    createWorkout: (source: "BLANK" | UUID) => void;
    options: Options;
}) {
    const dispatch = useAppDispatch();

    const selectWorkoutTemplate = async (
        workoutTemplate: server.journal.workouts.WorkoutTemplate.output,
    ) => {
        const result = await processResponse(
            server.journal.workouts.select_program_workout({
                source_workout: workoutTemplate.uuid,
                user_id: props.options.target.user_id,
                date: props.options.target.key,
            }),
        );

        if (result.type == "success") {
            const data = result.data;
            if (data == "SUBSCRIPTION_REQUIRED") {
                dispatch({type: "NEEDS_SUBSCRIPTION"});
                router.push("profile.subscription", {});
                return;
            }
            void analytics.track("USED_WORKOUT_TEMPLATE", {
                workout_template_id: workoutTemplate.id,
                workout_template_name: workoutTemplate.name,
            });
            props.options.updateJournal((prev) => ({
                ...prev,
                workout: data,
            }));
        }
    };

    const {activeProgram} = props;

    return (
        <ul
            role="list"
            className="
                grid auto-rows-fr grid-cols-1 gap-6
                sm:grid-cols-2
                lg:grid-cols-3
            "
        >
            <typography.ImportantAction
                onClick={() => props.createWorkout("BLANK")}
                title="Start from scratch"
            />

            {activeProgram != null ? (
                <typography.ImportantAction
                    onClick={() => {
                        props.options.router.setRoute("program");
                    }}
                    title="Use your program"
                />
            ) : (
                <typography.ImportantAction
                    onClick={() => {
                        router.push("create_workout_program.index", {});
                    }}
                    showIf={props.options.scope == "consumer"}
                    title="Use a program"
                />
            )}

            <typography.ImportantAction
                onClick={() => {
                    router.push("workouts.workouts", {});
                }}
                showIf={
                    props.options.scope == "client" || props.options.scope == "consumer"
                }
                title="Manage your templates"
            />
            {activeProgram == null &&
                props.options.program_templates.map((template) => (
                    <ProgramCard
                        key={template.id}
                        template={template}
                        target={props.options.target}
                        onSelect={(activeProgram) => {
                            props.options.updateJournal((prev) => ({
                                ...prev,
                                program: activeProgram,
                            }));
                            props.options.router.setRoute("program");
                        }}
                    />
                ))}
            {props.options.templates.map(annotateWithExercises).map((template) => (
                <typography.AsyncButton
                    key={template.id}
                    className="
                        col-span-1 rounded-lg border border-stone-200 bg-white text-left
                        shadow-xs
                    "
                    onClick={() => selectWorkoutTemplate(template)}
                >
                    <div
                        className="
                            flex w-full items-center justify-between space-x-6 p-6
                        "
                    >
                        <div className="flex-1 truncate">
                            <div className="flex items-center space-x-3">
                                <h3
                                    className="
                                        truncate text-sm font-medium text-gray-900
                                    "
                                >
                                    {template.name}
                                </h3>
                            </div>
                            <p className="mt-1 truncate text-sm text-gray-500">
                                {template.exercises.length == 1
                                    ? "1 exercise"
                                    : `${template.exercises.length} exercises`}

                                {template.user_id == null && (
                                    <span
                                        className="
                                            ml-2 inline-flex shrink-0 items-center
                                            rounded-full bg-green-50 px-1.5 py-0.5
                                            text-xs font-medium text-green-700 ring-1
                                            ring-green-600/20 ring-inset
                                        "
                                    >
                                        Sample
                                    </span>
                                )}

                                {template.user_id != null &&
                                    template.user_id !=
                                        props.options.target.user_id && (
                                        <span
                                            className="
                                                ml-2 inline-flex shrink-0 items-center
                                                rounded-full bg-sky-50 px-1.5 py-0.5
                                                text-xs font-medium text-sky-700 ring-1
                                                ring-sky-600/20 ring-inset
                                            "
                                        >
                                            Shared
                                        </span>
                                    )}
                            </p>
                        </div>
                    </div>
                </typography.AsyncButton>
            ))}
        </ul>
    );
}

const AnswerQuestion = (props: {
    readOnly?: boolean;
    question: server.journal.workouts.Workout.output["questions"][number];
    onSubmit: (answer: string) => void;
}) => {
    const [question, setQuestion] = React.useState(props.question);
    const form = forms.usePform(
        {
            answer: {type: "textarea"},
        },
        question,
        (values) => setQuestion({...question, ...values}),
    );

    return (
        <form
            className="flex flex-col gap-2 p-6"
            onSubmit={(event) => {
                event.preventDefault();
                props.onSubmit(form.values.answer);
            }}
        >
            <h1 className="text-xl">{props.question.type.name}</h1>
            <forms.PWidget
                field={produce(form.fields.answer, (draft) => {
                    draft.readOnly = props.readOnly;
                })}
            />
            <button
                type="submit"
                disabled={props.readOnly == true || form.values.answer == ""}
                className="
                    rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white
                    shadow-xs
                    hover:bg-indigo-500
                    focus-visible:outline-2 focus-visible:outline-offset-2
                    focus-visible:outline-indigo-600 focus-visible:outline-solid
                    disabled:border-slate-200 disabled:bg-slate-50
                    disabled:text-slate-500 disabled:shadow-none
                "
            >
                Submit
            </button>
        </form>
    );
};

export function Survey(props: {
    questions: server.journal.workouts.Workout.output["questions"];
    readOnly?: boolean;
    setQuestions: (questions: server.journal.workouts.Question.output[]) => void;
}) {
    const [isAnswering, setIsAnswering] = React.useState<UUID | null>(null);
    const questionToAnswer = props.questions.find(
        (question) => question.uuid == isAnswering,
    );

    return (
        <>
            {questionToAnswer != null && (
                <ContentModal isOpen={true} setOpen={() => setIsAnswering(null)}>
                    <AnswerQuestion
                        question={questionToAnswer}
                        readOnly={props.readOnly}
                        onSubmit={(answer) => {
                            props.setQuestions(
                                produce(props.questions, (draft) => {
                                    const question = draft.find(
                                        (q) => q.uuid == questionToAnswer.uuid,
                                    )!;
                                    question.answer = answer;
                                }),
                            );
                            setIsAnswering(null);
                        }}
                    />
                </ContentModal>
            )}
            <ul
                className="
                    mt-4 space-y-4
                    empty:mt-0
                "
            >
                {props.questions.map((question) => (
                    <li
                        key={question.uuid}
                        className={classNames(
                            "col-span-1 rounded-lg bg-white border border-stone-200 shadow-xs relative",
                        )}
                    >
                        <div
                            className="
                                flex w-full items-center justify-between space-x-4 p-4
                            "
                        >
                            <div className="flex-1 truncate">
                                <div className="flex items-center space-x-3">
                                    <h3
                                        className="
                                            truncate text-sm font-medium text-gray-900
                                        "
                                    >
                                        {question.type.name}
                                    </h3>
                                </div>
                                <p className="mt-1 truncate text-sm text-gray-500">
                                    {[].length == 3 && (
                                        <span
                                            className="
                                                inline-flex shrink-0 items-center
                                                rounded-full bg-yellow-50 px-1.5 py-0.5
                                                text-xs font-medium text-yellow-700
                                                ring-1 ring-yellow-600/20 ring-inset
                                            "
                                        >
                                            Optional
                                        </span>
                                    )}
                                </p>
                            </div>

                            {question.answer != "" ? (
                                <>
                                    <button
                                        type="button"
                                        className="
                                            rounded-md bg-white px-2 py-1.5 text-xs
                                            font-semibold text-gray-900 shadow-xs ring-1
                                            ring-stone-300 ring-inset
                                            hover:bg-stone-50
                                        "
                                        onClick={() => {
                                            setIsAnswering(question.uuid);
                                        }}
                                    >
                                        View
                                    </button>
                                    <div
                                        className="
                                            mx-auto flex size-8 items-center
                                            justify-center rounded-full bg-green-100
                                        "
                                    >
                                        <CheckIcon
                                            className="size-5 text-green-600"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => {
                                            setIsAnswering(question.uuid);
                                        }}
                                        type="button"
                                        className="
                                            rounded-md bg-indigo-600 px-2 py-1.5 text-xs
                                            font-semibold text-white shadow-xs
                                            hover:bg-indigo-500
                                            focus-visible:outline-2
                                            focus-visible:outline-offset-2
                                            focus-visible:outline-indigo-600
                                            focus-visible:outline-solid
                                        "
                                    >
                                        Answer
                                    </button>
                                    <div
                                        className="
                                            mx-auto flex size-8 items-center
                                            justify-center rounded-full bg-gray-100
                                        "
                                    ></div>
                                </>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </>
    );
}

function UpdateWorkout(props: {
    workout: server.journal.workouts.Workout.output;
    options: Options;
}) {
    const {startRestTimer} = props.options;
    const handlers = useWorkout({
        workout: {
            questions: props.workout.questions,
            sections: props.workout.sections,
        },
        exercise_maxes: props.options.exercise_maxes,
        setWorkout: (value) => {
            props.options.updateJournal((prev) => ({
                ...prev,
                workout: prev.workout == null ? null : value(prev.workout),
            }));
        },
    });
    const [isPickingDate, setIsPickingDate] = React.useState(false);
    const [isViewingInstructions, setIsViewingInstructions] = React.useState(false);
    const dispatch = useAppDispatch();

    const completeDialog = dialogs.useDialog({
        Content: () => <div></div>,
    });

    const initial = React.useRef(true);

    const {flush} = useSerializedSave(
        async () => {
            if (initial.current == true) {
                initial.current = false;
                return;
            }

            props.options.setResolved?.({
                workout: {
                    ...props.workout,
                    sections: handlers.sections,
                    questions: handlers.questions,
                },
            });

            await server.journal.workouts.update_workout({
                uuid: props.workout.uuid,
                sections: handlers.sections,
                questions: handlers.questions,
                template: null,
            });
        },
        initial.current == true ? 0 : 500,
        [handlers.sections, handlers.questions],
    );

    const [isCreatingTemplate, setIsCreatingTemplate] = React.useState(false);

    const [template, setTemplate] = React.useState({name: ""});

    const templateForm = forms.usePform(
        {
            name: {type: "text"},
        },
        template,
        setTemplate,
    );

    const templateModal = (
        <ContentModal isOpen={isCreatingTemplate} setOpen={setIsCreatingTemplate}>
            <form
                autoComplete="off"
                noValidate
                onSubmit={async (event) => {
                    event.preventDefault();
                    void analytics.track("CREATED_TEMPLATE_FROM_WORKOUT");

                    const response =
                        await server.journal.workouts.create_template_from_workout({
                            ...templateForm.values,
                            source_workout: props.workout.uuid,
                        });

                    if (response.type == "success") {
                        setIsCreatingTemplate(false);
                        if (props.options.scope == "manager") {
                            alert("Template created. You can use it in your programs.");
                        } else {
                            router.push("workouts.workouts", {});
                            dispatch(notify("Template created"));
                        }
                    } else if (response.type == "invalid") {
                        const errors: {loc: string[]; msg: string}[] = response.errors;

                        const errorsMap = Object.fromEntries(
                            errors
                                .filter((e) => e.loc.length == 1)
                                .map((e) => [e.loc[0], [e.msg]]),
                        );
                        templateForm.setErrors(errorsMap);
                    }
                }}
                className="flex flex-col gap-4 p-6"
            >
                <h2 className="text-xl whitespace-nowrap">Create template</h2>

                <forms.PField field={templateForm.fields.name} />

                <button
                    type="submit"
                    disabled={templateForm.values.name == ""}
                    className="
                        rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold
                        text-white shadow-xs
                        hover:bg-indigo-500
                        focus-visible:outline-2 focus-visible:outline-offset-2
                        focus-visible:outline-indigo-600 focus-visible:outline-solid
                        disabled:border-slate-200 disabled:bg-slate-50
                        disabled:text-slate-500 disabled:shadow-none
                    "
                >
                    Create
                </button>
            </form>
        </ContentModal>
    );

    const percentageExercises =
        [].length > 1 && props.workout.maxes != "AUTOMATIC"
            ? getPercentageExercises(handlers.exercises)
            : [];

    const requiresMaxes = percentageExercises.some(
        (exerciseType) =>
            props.options.exercise_maxes.actual.find(
                (max) => max.exercise_type.uuid == exerciseType.uuid,
            ) == null,
    );

    const [exerciseMaxes, setExerciseMaxes] = React.useState(
        percentageExercises.reduce<Record<UUID, string | null>>((acc, exerciseType) => {
            const existingMax = props.options.exercise_maxes.actual.find(
                (max) => max.exercise_type.uuid == exerciseType.uuid,
            );

            acc[exerciseType.uuid] = existingMax?.weight ?? null;
            return acc;
        }, {}),
    );

    const requiresMaxesInput = Object.values(exerciseMaxes).some(
        (max) => max == null || max == "",
    );

    const [showMaxes, setShowMaxes] = React.useState(
        requiresMaxesInput ||
            (percentageExercises.length > 0 && props.workout.maxes == "UPDATE_MAXES"),
    );

    const isImperial = forms.useIsImperial();

    const confirmMaxes = () => {
        const payload = Object.entries(exerciseMaxes).map(([uuid, weight]) => ({
            exercise_type: {uuid: uuid as UUID},
            weight: weight ?? "",
            type: "MAX" as const,
            reps: 1,
            workout: {
                uuid: props.workout.uuid,
            },
        }));
        if (requiresMaxes) {
            void server.journal.workouts.set_exercise_maxes(payload);
        }
        setShowMaxes(false);

        handlers.exercises.forEach((exercise) => {
            if (exercise.type.uuid in exerciseMaxes) {
                exercise.sets.forEach((set) => {
                    if (
                        set.weight_type == "PERCENTAGE" &&
                        set.weight_percentage != null
                    ) {
                        const liftMax = exerciseMaxes[exercise.type.uuid]!;
                        const weight = utils
                            .roundNumberToNearest(
                                new Decimal(liftMax)
                                    .times(set.weight_percentage)
                                    .dividedBy("100")
                                    .dividedBy(isImperial ? "0.45359237" : "1"),
                                (isImperial
                                    ? new Decimal("2.5")
                                    : new Decimal("1")
                                ).times(2),
                            )
                            .times(isImperial ? "0.45359237" : "1");
                        set.__update({
                            weight_type: "VALUE",
                            weight: weight.toString(),
                        });
                    }
                });
            }
        });
    };

    React.useEffect(() => {
        if (showMaxes == false && percentageExercises.length > 0) {
            confirmMaxes();
        }
    }, []);

    const readOnly = props.workout.status == "COMPLETE" || requiresMaxes;

    const maxes = showMaxes ? (
        <div className="space-y-2">
            <h2 className="text-xl">Your Maxes</h2>
            <div
                className="
                    grid grid-cols-1 gap-4
                    sm:grid-cols-2
                "
            >
                {percentageExercises.map((exerciseType) => (
                    <div
                        key={exerciseType.uuid}
                        className="rounded-lg bg-orange-100 px-4 py-3"
                    >
                        <h2 className="text-base">{exerciseType.name}</h2>
                        <div className="mt-1 flex items-center gap-2">
                            <widgets.WeightInput
                                isImperial={isImperial}
                                name={exerciseType.uuid}
                                value={exerciseMaxes[exerciseType.uuid] ?? ""}
                                onChange={(value) => {
                                    setExerciseMaxes(
                                        produce(exerciseMaxes, (draft) => {
                                            draft[exerciseType.uuid] = value;
                                        }),
                                    );
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <button
                type="button"
                onClick={() => confirmMaxes()}
                disabled={requiresMaxesInput}
                className={classNames(
                    "rounded-sm bg-indigo-600 disabled:opacity-70 px-4 py-1 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
                )}
            >
                Confirm
            </button>
        </div>
    ) : null;

    return (
        <>
            <div className="-mt-6"></div>
            {templateModal}
            {completeDialog.dialog}
            <WorkoutActions
                actions={[
                    {
                        icon: "copy",
                        title: "Copy workout",
                        onClick: () => {
                            setIsPickingDate(true);
                        },
                    },
                    {
                        icon: "0184-files",
                        title: "Create template",
                        onClick: () => {
                            setIsCreatingTemplate(true);
                        },
                    },
                    {
                        icon: "0018-eraser",
                        title: "Delete workout",
                        onClick: async () => {
                            return props.options.deleteWorkout();
                        },
                    },
                ]}
            />

            <Dialogs
                isShowingAlertDialog={false}
                isShowingConfirmDialog={false}
                isShowingDateDialog={
                    isPickingDate == true ? props.options.target.key : false
                }
                didConfirmDialog={false}
                pickedDate={props.options.target.key}
                message="Something else"
                body="Pick something"
                confirmConfirmDialog={() => {}}
                hideAlertDialog={() => {}}
                hideConfirmDialog={() => {}}
                hideActiveHelp={() => {}}
                hideDateDialog={() => setIsPickingDate(false)}
                pickDateForDateDialog={async (pickedDate) => {
                    setIsPickingDate(false);
                    const key = dates.formatISO(pickedDate, {
                        representation: "date",
                    });

                    void analytics.track("COPIED_WORKOUT");
                    await processResponse(
                        server.journal.workouts.copy_workout({
                            date: key,
                            user_id: props.options.target.user_id,
                            source_workout_uuid: props.workout.uuid,
                        }),
                    );
                    void props.options.onCopy(key);
                }}
            />
            {maxes}
            {props.workout.instructions != "" && (
                <div className="flex justify-center">
                    <button
                        type="button"
                        className="
                            w-full rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm
                            font-semibold text-white shadow-xs
                            hover:bg-indigo-500
                            focus-visible:outline-2 focus-visible:outline-offset-2
                            focus-visible:outline-indigo-600 focus-visible:outline-solid
                            disabled:border-slate-200 disabled:bg-slate-50
                            disabled:text-slate-500 disabled:shadow-none
                            sm:max-w-sm
                        "
                        onClick={() => setIsViewingInstructions(true)}
                    >
                        Workout instructions
                    </button>

                    <InstructionsModal
                        name="Instructions"
                        video_url={null}
                        instructions={props.workout.instructions}
                        isOpen={isViewingInstructions}
                        onClose={() => setIsViewingInstructions(false)}
                    />
                </div>
            )}
            <div className={classNames(showMaxes && "opacity-50")}>
                <WorkoutContext.Provider
                    value={{
                        workout: props.workout,
                        scope: props.options.scope,
                        startRestTimer,
                    }}
                >
                    <Workout
                        workout_uuid={props.workout.uuid}
                        handlers={handlers}
                        isTemplate={false}
                        readOnly={readOnly || undefined}
                    />
                </WorkoutContext.Provider>
            </div>
            <Survey
                questions={handlers.questions}
                readOnly={readOnly}
                setQuestions={handlers.setQuestions}
            />
            <div className="mt-4 flex flex-col items-center">
                <button
                    type="button"
                    disabled={requiresMaxes}
                    className="
                        w-full rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm
                        font-semibold text-white shadow-xs
                        hover:bg-indigo-500
                        focus-visible:outline-2 focus-visible:outline-offset-2
                        focus-visible:outline-indigo-600 focus-visible:outline-solid
                        disabled:opacity-70
                        sm:max-w-sm
                    "
                    {...(props.workout.status == "INCOMPLETE"
                        ? {
                              children: "Complete workout",
                              onClick: () => {
                                  void hardware.endLiveActivity();
                                  props.options.startRestTimer(null);

                                  void (async () => {
                                      await flush();
                                      void server.journal.workouts.complete_workout({
                                          workout_uuid: props.workout.uuid,
                                      });
                                      dispatch(showFeelerIfFirstTime());
                                      props.options.updateJournal((prev) => ({
                                          ...prev,
                                          workout: {
                                              ...props.workout,
                                              status: "COMPLETE",
                                          },
                                      }));

                                      void analytics.track("COMPLETED_WORKOUT", {
                                          workout_template_name:
                                              props.workout.name ?? undefined,
                                      });
                                  })();
                              },
                          }
                        : {
                              children: "Edit workout",
                              onClick: () => {
                                  void server.journal.workouts.incomplete_workout({
                                      workout_uuid: props.workout.uuid,
                                  });
                                  props.options.updateJournal((prev) => ({
                                      ...prev,
                                      workout: {...props.workout, status: "INCOMPLETE"},
                                  }));
                              },
                          })}
                />
            </div>
        </>
    );
}

export function JournalWorkout(props: JournalProps) {
    const scope = props.profile.user.is_client == true ? "client" : "consumer";
    const target = {
        user_id: props.profile.user.id,
        key: props.resolved.journal.day.key,
    };
    const router = {
        route: props.workoutRoute,
        setRoute: props.setWorkoutRoute,
    };
    const updateJournal = (
        updates: (
            prev: server.journal.day.Journal.output,
        ) => server.journal.day.Journal.output,
    ) => {
        props.setResolved((prevState) => ({
            journal: updates(prevState.journal),
        }));
    };

    if (props.resolved.journal.workout != null) {
        if (props.resolved.journal.workout.mode == "GUIDED") {
            return (
                <GuidedWorkout
                    key={`workout-${props.resolved.journal.workout.uuid}`}
                    journal={props.resolved.journal}
                    workout={props.resolved.journal.workout}
                    updateJournal={(updates) => {
                        props.setResolved((prevState) => ({
                            journal: updates(prevState.journal),
                        }));
                    }}
                    switchTab={props.switchTab}
                    deleteWorkout={props.deleteWorkout}
                    startRestTimer={props.startRestTimer}
                    restTimer={props.restTimer}
                    scope={scope}
                />
            );
        }
        return (
            <UpdateWorkout
                workout={props.resolved.journal.workout}
                options={{
                    updateJournal,
                    switchTab: props.switchTab,
                    router,
                    program_templates: props.resolved.journal.program_templates,
                    exercise_maxes: props.resolved.journal.exercise_maxes,
                    templates: props.resolved.journal.workout_templates,
                    onCopy: () => {},
                    setResolved: () => {},
                    scope,
                    target,
                    startRestTimer: props.startRestTimer,
                    deleteWorkout: props.deleteWorkout,
                }}
            />
        );
    }

    if (props.workoutRoute == "custom" || props.resolved.journal.program == null) {
        return (
            <CreateWorkout
                activeProgram={props.resolved.journal.program}
                createWorkout={props.createWorkout}
                options={{
                    updateJournal,
                    switchTab: props.switchTab,
                    router,
                    program_templates: props.resolved.journal.program_templates,
                    exercise_maxes: props.resolved.journal.exercise_maxes,
                    templates: props.resolved.journal.workout_templates,
                    onCopy: () => {},
                    setResolved: () => {},
                    scope,
                    target,
                    startRestTimer: props.startRestTimer,
                    deleteWorkout: props.deleteWorkout,
                }}
            />
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <ProgramWizard
                next_workout={props.resolved.journal.next_workout}
                scope={scope}
                program={props.resolved.journal.program}
                onSelectWorkout={(_, selected) => {
                    updateJournal((prev) => ({
                        ...prev,
                        workout: selected,
                    }));
                }}
                target={target}
            />
            <typography.ImportantAction
                onClick={() => props.setWorkoutRoute("custom")}
                title="Custom workout"
            />
        </div>
    );
}
